import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search,
  Visibility,
  Report,
  CheckCircle,
  PendingActions,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface ReportItem {
  reportId: string;
  reporterId: string;
  reporterFullname: string;
  reporterProfileImage: string;
  reportedUserId: string;
  reportedUserFullname: string;
  reportedUserProfileImage: string;
  reportType: string;
  reportDescription: string;
  isResolved: boolean;
  createdAt: string;
}

interface ReportStats {
  pending: number;
  resolved: number;
  total: number;
}

const ReportList: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [stats, setStats] = useState<ReportStats>({ pending: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReports, setTotalReports] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [reportType, setReportType] = useState('ALL');

  useEffect(() => {
    fetchReports();
  }, [page, rowsPerPage, search, status, reportType]);

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) params.search = search;
      if (status !== 'ALL') params.status = status;
      if (reportType !== 'ALL') params.reportType = reportType;

      const response = await adminService.getAllReports(params);

      if (response.status) {
        setReports(response.data?.reports || []);
        setStats(response.data?.stats || { pending: 0, resolved: 0, total: 0 });
        setTotalReports(response.data?.pagination?.totalReports || 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Raporlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const getReportTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      USER: 'Kullanıcı',
      POST: 'Gönderi',
      MESSAGE: 'Mesaj',
      COMMENT: 'Yorum',
      SUB_COMMENT: 'Alt Yorum',
      SPAM: 'Spam',
      GENERAL: 'Genel',
      OTHER: 'Diğer',
    };
    return types[type] || type;
  };

  const getReportTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' } = {
      USER: 'error',
      POST: 'warning',
      MESSAGE: 'info',
      COMMENT: 'primary',
      SUB_COMMENT: 'primary',
      SPAM: 'error',
      GENERAL: 'default',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Rapor Yönetimi
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Kullanıcı raporlarını görüntüleyin ve yönetin.
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: '#fff3e0',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PendingActions sx={{ color: '#f57c00', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bekleyen Raporlar
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: '#e8f5e9',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle sx={{ color: '#2e7d32', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.resolved}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Çözümlenen Raporlar
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: '#e3f2fd',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Report sx={{ color: '#1976d2', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Rapor
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Kullanıcı adı veya reportId ile ara..."
            value={search}
            onChange={handleSearchChange}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={status}
              label="Durum"
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="ALL">Tümü</MenuItem>
              <MenuItem value="PENDING">Bekleyen</MenuItem>
              <MenuItem value="RESOLVED">Çözümlenen</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Rapor Tipi</InputLabel>
            <Select
              value={reportType}
              label="Rapor Tipi"
              onChange={(e) => {
                setReportType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="ALL">Tümü</MenuItem>
              <MenuItem value="USER">Kullanıcı</MenuItem>
              <MenuItem value="POST">Gönderi</MenuItem>
              <MenuItem value="MESSAGE">Mesaj</MenuItem>
              <MenuItem value="COMMENT">Yorum</MenuItem>
              <MenuItem value="SUB_COMMENT">Alt Yorum</MenuItem>
              <MenuItem value="SPAM">Spam</MenuItem>
              <MenuItem value="GENERAL">Genel</MenuItem>
              <MenuItem value="OTHER">Diğer</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Raporlayan</TableCell>
                <TableCell>Raporlanan</TableCell>
                <TableCell>Rapor Tipi</TableCell>
                <TableCell>Açıklama</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Tarih</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Rapor bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow
                    key={report.reportId}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={report.reporterProfileImage || ''} alt={report.reporterFullname}>
                          {report.reporterFullname?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {report.reporterFullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.reporterId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={report.reportedUserProfileImage || ''} alt={report.reportedUserFullname}>
                          {report.reportedUserFullname?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {report.reportedUserFullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.reportedUserId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReportTypeLabel(report.reportType)}
                        color={getReportTypeColor(report.reportType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.reportDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.isResolved ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Çözümlendi"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<PendingActions />}
                          label="Beklemede"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/admin/reports/${report.reportId}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalReports}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>
    </Container>
  );
};

export default ReportList;

